/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useState, useMemo } from "react";
import { BEM } from "@adapptio/lib-components";
import { useSimpleEvent } from "../../../../ui/hooks/useSimpleEvent";
import { SearchBox, SEARCHBOX_SIZE, SEARCHBOX_STYLE } from "../../../../ui/components/SearchBox";
import { DocumentationNav } from "./DocumentationNav";
import { DocumentationView } from "../DocumentationView";
import { buildNavTree, filterNav } from "../helpers";

export interface IDocumentationSidebarProps {
	view: DocumentationView;
}

export const DocumentationSidebar: React.FC<IDocumentationSidebarProps> = ({ view }) => {
	const baseClass = BEM("edt-documentation-sidebar");
	baseClass.add("edt-base");
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedPath, setSelectedPath] = useState<string | null>(view.getSelected());

	// Update local state when selection changes
	useSimpleEvent(view.onSelectedChange, () => {
		setSelectedPath(view.getSelected());
	}, [view], true);

	// Build nav tree from docs registry
	const navTree = useMemo(() => {
		return buildNavTree(view.getDocsRegistry());
	}, [view]);

	// Filter navigation based on search query
	const nav = useMemo(() => {
		if (searchQuery) {
			return filterNav(navTree, searchQuery);
		}
		return navTree;
	}, [navTree, searchQuery]);

	const handleSearch = (value: string) => {
		setSearchQuery(value);
	};

	const handleSetSelected = (path: string) => {
		view.setSelected(path);
	};

	return (
		<div className={baseClass.toClassName()}>
			<SearchBox
				value={searchQuery}
				onChange={handleSearch}
				size={SEARCHBOX_SIZE.SMALL}
				style={SEARCHBOX_STYLE.FIELD_NAKED_ROUND}
				placeholder="Search..."
			/>
			{nav.map((item) => (
				<DocumentationNav
					key={item.path}
					item={item}
					selected={selectedPath}
					setSelected={handleSetSelected}
					level={0}
				/>
			))}
		</div>
	);
};
